import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Search, MoreVertical, Pencil, Copy, Trash2, FileText, ExternalLink, Link as LinkIcon } from "lucide-react";
import { usePublishedMenus, useToggleMenuPublished, useDeletePublishedMenu, useDuplicatePublishedMenu, type PublishedMenuRow } from "@/hooks/usePublishedMenus";
import { publicMenuUrl } from "@/lib/public-urls";
import { toast } from "sonner";

type StatusFilter = "all" | "published" | "draft";

export default function MenusAdminContent() {
  const navigate = useNavigate();
  const { data: menus, isLoading } = usePublishedMenus();
  const togglePublished = useToggleMenuPublished();
  const deleteMenu = useDeletePublishedMenu();
  const duplicateMenu = useDuplicatePublishedMenu();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [deleteTarget, setDeleteTarget] = useState<PublishedMenuRow | null>(null);

  const filtered = useMemo(() => {
    if (!menus) return [];
    let result = menus;
    if (statusFilter === "published") result = result.filter((m) => m.is_published);
    if (statusFilter === "draft") result = result.filter((m) => !m.is_published);
    if (search) {
      const s = search.toLowerCase();
      result = result.filter((m) => m.title.toLowerCase().includes(s) || m.description?.toLowerCase().includes(s));
    }
    return result;
  }, [menus, search, statusFilter]);

  const counts = useMemo(() => {
    if (!menus) return { all: 0, published: 0, draft: 0 };
    return {
      all: menus.length,
      published: menus.filter((m) => m.is_published).length,
      draft: menus.filter((m) => !m.is_published).length,
    };
  }, [menus]);

  const copyLink = (slug: string) => {
    navigator.clipboard.writeText(publicMenuUrl(slug));
    toast.success("Link copied!");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-muted-foreground text-sm">
          Build and publish public-facing menus to drive leads.
        </p>
        <Button onClick={() => navigate("/admin/menus/new")}>
          <Plus className="h-4 w-4 mr-2" /> Create Menu
        </Button>
      </div>

      {(menus?.length ?? 0) > 0 && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <TabsList>
              <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
              <TabsTrigger value="published">Published ({counts.published})</TabsTrigger>
              <TabsTrigger value="draft">Draft ({counts.draft})</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-full sm:w-[200px]" />
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : !menus?.length ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-display text-lg font-semibold mb-1">No menus yet</h3>
            <p className="text-muted-foreground text-sm max-w-md">Create public-facing menus to showcase your offerings and capture leads.</p>
            <Button className="mt-4" onClick={() => navigate("/admin/menus/new")}>
              <Plus className="h-4 w-4 mr-2" /> Create Your First Menu
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((menu) => (
              <MenuCard
                key={menu.id}
                menu={menu}
                onEdit={() => navigate(`/admin/menus/${menu.id}/edit`)}
                onDuplicate={() => duplicateMenu.mutate(menu.id)}
                onDelete={() => setDeleteTarget(menu)}
                onTogglePublished={(checked) => togglePublished.mutate({ id: menu.id, is_published: checked })}
                onCopyLink={() => copyLink(menu.slug)}
              />
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm">No menus match your filters.</div>
          )}
        </>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. All sections and items in this menu will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteTarget) deleteMenu.mutate(deleteTarget.id); setDeleteTarget(null); }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface MenuCardProps {
  menu: PublishedMenuRow;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onTogglePublished: (checked: boolean) => void;
  onCopyLink: () => void;
}

function MenuCard({ menu, onEdit, onDuplicate, onDelete, onTogglePublished, onCopyLink }: MenuCardProps) {
  return (
    <Card className="group hover:shadow-md transition-shadow cursor-pointer overflow-hidden" onClick={onEdit}>
      {menu.cover_image_url && (
        <div className="h-32 w-full overflow-hidden">
          <img
            src={menu.cover_image_url}
            alt={menu.title}
            className="h-full w-full object-cover"
          />
        </div>
      )}
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-semibold text-foreground truncate">{menu.title}</h3>
            <a
              href={`/menu/${menu.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-muted-foreground hover:text-primary hover:underline transition-colors"
            >
              /menu/{menu.slug} <ExternalLink className="inline h-3 w-3 ml-0.5" />
            </a>
          </div>
          <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
            <Switch checked={menu.is_published} onCheckedChange={onTogglePublished} className="scale-90" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  <Pencil className="h-4 w-4 mr-2" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDuplicate}>
                  <Copy className="h-4 w-4 mr-2" /> Duplicate
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onCopyLink}>
                  <LinkIcon className="h-4 w-4 mr-2" /> Copy Link
                </DropdownMenuItem>
                {menu.is_published && (
                  <DropdownMenuItem asChild>
                    <a href={`/menu/${menu.slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <ExternalLink className="h-4 w-4 mr-2" /> View Public Page
                    </a>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {menu.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">{menu.description}</p>
        )}

        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground pt-1">
          <Badge variant={menu.show_prices ? "default" : "secondary"} className="text-xs">
            Prices {menu.show_prices ? "Visible" : "Hidden"}
          </Badge>
          <div className="flex items-center gap-1">
            {menu.is_published && menu.is_listed && (
              <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                Listed
              </Badge>
            )}
            {menu.is_published && !menu.is_listed && (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                Unlisted
              </Badge>
            )}
            {!menu.is_published && (
              <Badge variant="secondary" className="text-xs">Draft</Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
